// ============================================================
// billingUi.tsx — Invoicing Stage 1 shared atoms, matched to the approved
// mockup (outputs/invoicing-stage1/invoicing-stage1-mockups.html, kit.py):
// status pills (.st draft/appr/iss/part/paid/over/void), VAT chips (.vat
// none/std/cost), the hero band, KPI tiles, money/date formatters and the
// Feather-style icons the billing screens use. Tokens only (design-language
// §5) — the forest hero/balance bands are the documented literal-hex
// exception (fixed colour in both themes, §3.2).
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { BTN_PRIMARY } from '../../styles/buttons'
import WithheldAmount from '../shared/WithheldAmount'
import type { BillStatus, BillType, CostsFormat, VatTreatment } from '../../types/billing.types'

// ── formatters (display only — every figure arrives server-computed) ──
export const money = (pence: number): string =>
  `${pence < 0 ? '−' : ''}£${(Math.abs(pence) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
export const ratePerHour = (unitRatePence: number | null): string =>
  // Display only: whole-pound rates show as "£250/hr"; anything with pence
  // always shows both digits ("£201.10/hr", never "£201.1/hr").
  unitRatePence == null ? '' : `£${((unitRatePence * 10) / 100).toLocaleString('en-GB', (unitRatePence * 10) % 100 === 0 ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/hr`
export const fmtDate = (iso: string | null | undefined, style: 'short' | 'long' | 'day' = 'short'): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  if (style === 'day') return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: style === 'long' ? 'long' : 'short', year: 'numeric' })
}
export const fmtDuration = (seconds: number): string => {
  const m = Math.round(seconds / 60)
  const h = Math.floor(m / 60)
  return h ? `${h}h ${m % 60}m` : `${m}m`
}
/** "12.30" → 1230. Rejects anything that isn't money. Pure string → integer
 *  pence (no float maths): accepts "12", "12.3", "12.30", "12." and ".5". */
export const parsePounds = (raw: string): number | null => {
  const t = raw.replace(/[£,\s]/g, '')
  // ≤ 12 integer digits keeps Number() exact (the server caps bills far lower).
  if (!/^\d{0,12}(\.\d{0,2})?$/.test(t) || !/\d/.test(t)) return null
  const [i = '', f = ''] = t.split('.')
  return Number(i || '0') * 100 + Number((f + '00').slice(0, 2))
}
/** 1234 → "12.34", -5 → "-0.05". Integer maths only (the inverse of parsePounds). */
export const penceToPoundsText = (pence: number): string => {
  const a = Math.abs(Math.trunc(pence))
  return `${pence < 0 ? '-' : ''}${Math.floor(a / 100)}.${String(a % 100).padStart(2, '0')}`
}
/** Keep only what a money field accepts: digits, ONE decimal point, at most
 *  2 decimal places (a 3rd typed/pasted decimal is dropped, never rounded),
 *  and — only when allowed — a single leading minus. Strips £ , and spaces. */
export const sanitizeMoneyText = (raw: string, allowNegative = false): string => {
  const neg = allowNegative && raw.trim().startsWith('-')
  let t = raw.replace(/[^\d.]/g, '')
  const dot = t.indexOf('.')
  if (dot !== -1) t = `${t.slice(0, dot + 1)}${t.slice(dot + 1).replace(/\./g, '').slice(0, 2)}`
  return `${neg ? '-' : ''}${t}`
}

/** The largest amount a settlement money field holds — the server's ceiling
 *  (a Postgres Int of pence: £21,474,836.47). */
export const MAX_INPUT_PENCE = 2_147_483_647
/** STRICT money parse for fields that must never reinterpret what was typed
 *  (the settlement figures). Allowed: digits, "£", commas, spaces and ONE
 *  decimal point, with at most 2 decimal places. Nothing is stripped or
 *  rounded — "-5", "1e3", "abc" and "1.005" are errors, worded for the
 *  person. Blank → { blank: true }. Integer string maths, no floats. */
export type StrictMoney = { ok: true; pence: number } | { ok: false; blank: true; error?: undefined } | { ok: false; blank?: false; error: string }
export const parsePoundsStrict = (raw: string, maxPence = MAX_INPUT_PENCE): StrictMoney => {
  const t = raw.trim()
  if (t === '') return { ok: false, blank: true }
  if (/[^\d£,\s.]/.test(t)) return { ok: false, error: 'Use numbers only, e.g. 1,250.00 — no minus signs, letters or other symbols.' }
  if ((t.match(/\./g) ?? []).length > 1) return { ok: false, error: 'Use one decimal point at most.' }
  const s = t.replace(/[£,\s]/g, '')
  if (!/\d/.test(s)) return { ok: false, error: 'Enter an amount, e.g. 1250.00' }
  const [i = '', f = ''] = s.split('.')
  if (f.length > 2) return { ok: false, error: 'Use no more than 2 decimal places (whole pence).' }
  const over = { ok: false as const, error: `That’s over the maximum of ${money(maxPence)}.` }
  // > 13 significant integer digits can't be under the maximum — and would
  // lose precision as a Number.
  if (i.replace(/^0+/, '').length > 13) return over
  const pence = Number(i || '0') * 100 + Number((f + '00').slice(0, 2))
  return pence > maxPence ? over : { ok: true, pence }
}
/** 20% VAT on net pence — mirrors the backend's invoiceMath standardVat
 *  EXACTLY (integer maths, half away from zero), so a VAT hint shows the
 *  figure a bill would. */
export const standardVatPence = (netPence: number): number => {
  const n = netPence * 20
  const q = Math.trunc(n / 100)
  const r = n - q * 100
  return Math.abs(r) * 2 >= 100 ? q + Math.sign(n) : q
}

export const BILL_TYPE_LABEL: Record<BillType, string> = {
  ON_ACCOUNT: 'On account', INTERIM_STATUTE: 'Interim statute', FINAL_STATUTE: 'Final statute',
}
export const BILL_TYPE_SHORT: Record<BillType, string> = { ON_ACCOUNT: 'On account', INTERIM_STATUTE: 'Interim', FINAL_STATUTE: 'Final' }
export const COSTS_FORMAT_LABEL: Record<CostsFormat, string> = { ITEMISED: 'Itemised', BY_FEE_EARNER: 'By fee earner', SINGLE_LINE: 'Single line' }

// Every signed-in member can use billing; WHICH bills they see is decided
// server-side (lib/billingAccess.ts: cases they can access, or every bill with
// "view all billing" / admin).
export const useCanUseBilling = (): boolean => useCasevoStore((s) => !!s.authUser)

// ── icons (Feather, inline — design-language §6) ──
const P: Record<string, React.ReactNode> = {
  bill: <><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2l-1 .7-3-2-3 2-3-2-3 2-3-2z" /><path d="M8 8h8M8 12h8M8 16h4" /></>,
  card: <><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  pen: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></>,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  x: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  dl: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>,
  send: <><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>,
  chev: <polyline points="6 9 12 15 18 9" />,
  chevr: <polyline points="9 18 15 12 9 6" />,
  search: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
  alert: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  check: <polyline points="20 6 9 17 4 12" />,
  eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></>,
  info: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
  zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  undo: <><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></>,
  print: <><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>,
  mail: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>,
  phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />,
  memo: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>,
  cal: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  more: <><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
}
export type BIconName = keyof typeof P
export const BIcon: React.FC<{ name: BIconName; size?: number; className?: string; strokeWidth?: number }> = ({ name, size = 15, className = '', strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}>{P[name]}</svg>
)

// ── buttons (design-language §3.3) ──
const BTN = 'inline-flex items-center justify-center gap-[7px] whitespace-nowrap font-medium border transition-colors focus:outline-none casevo-focus disabled:opacity-50 disabled:cursor-not-allowed'
export const B_PRIMARY = `${BTN_PRIMARY} gap-[7px] h-9 px-[14px] rounded-[10px] text-[13.5px] border border-casevo-primary`
export const B_PRIMARY_LG = `${BTN_PRIMARY} gap-[7px] h-[42px] px-5 rounded-[11px] text-[14px] border border-casevo-primary`
export const B_SECONDARY = `${BTN} h-9 px-[14px] rounded-[10px] text-[13.5px] border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid`
export const B_SECONDARY_SM = `${BTN} h-[30px] px-[10px] rounded-[8px] text-[12.5px] border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid`
export const B_DANGER = `${BTN} h-9 px-[14px] rounded-[10px] text-[13.5px] border-casevo-border bg-casevo-surface text-casevo-danger hover:border-casevo-danger`
export const B_GHOST_SM = `${BTN} h-[30px] px-[8px] rounded-[8px] text-[12.5px] border-transparent bg-transparent text-casevo-brand dark:text-[#8fd6b0] hover:bg-casevo-mint`
export const B_ICON = 'w-[30px] h-[30px] rounded-[8px] inline-grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand transition-colors focus:outline-none casevo-focus disabled:opacity-40'

// Hero-band buttons (white-alpha on the forest band).
export const B_HERO = 'inline-flex items-center gap-[7px] h-8 px-3 rounded-[9px] bg-white/[0.14] hover:bg-white/25 text-white text-[13px] font-medium border border-white/[0.14] focus:outline-none casevo-focus whitespace-nowrap'

// ── cards / text ──
export const CARD = 'bg-casevo-surface border border-casevo-border rounded-[14px] shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
export const EYEBROW = 'text-[11px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft'
export const NOTE = 'text-[12.5px] text-casevo-text-soft leading-[1.45]'
export const TH = 'text-[10.5px] tracking-[0.14em] uppercase text-casevo-text-soft font-bold text-left px-[10px] py-[9px] border-b border-casevo-border whitespace-nowrap'
export const TD = 'px-[10px] py-[11px] border-b border-casevo-border-soft align-middle'
export const INPUT = 'h-[38px] rounded-[9px] border border-casevo-border bg-casevo-input px-3 text-[14px] text-casevo-forest placeholder:text-casevo-text-soft focus:outline-none focus:border-casevo-brand focus:bg-casevo-surface focus:ring-[3px] focus:ring-casevo-brand/15 w-full'
export const LABEL = 'block text-[12px] font-semibold text-casevo-text-soft mb-[5px]'
// Field error line — same look as settingsUi S_FIELD_ERROR (12px, casevo-danger).
export const FIELD_ERROR = 'block text-[12px] text-casevo-danger mt-[5px]'

/**
 * MoneyInput — THE money-entry field for billing. A non-editable "£" is pinned
 * inside the left of the field (a separate, pointer-events-none, aria-hidden
 * element — it isn't part of the value, so it can't be deleted, selected or
 * typed over); the user only ever types the number. Value is the pounds TEXT
 * (e.g. "12.34") exactly as before — callers keep converting with parsePounds
 * (string → integer pence, no float). Typing/paste keeps digits + one "." +
 * max 2 decimals (and a leading "-" when allowNegative); "£", commas and
 * spaces are stripped. Right-aligned; on blur a valid amount is normalised to
 * 2 decimal places ("12.5" → "12.50"). `error` renders the field-error line
 * and marks the input aria-invalid.
 */
export const MoneyInput: React.FC<{
  value: string
  onChange: (poundsText: string) => void
  ariaLabel?: string
  id?: string
  error?: string | null
  allowNegative?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
  autoFocus?: boolean
  // STRICT: pass the text through exactly as typed (no stripping); the
  // caller validates with parsePoundsStrict and shows its error.
  strict?: boolean
}> = ({ value, onChange, ariaLabel, id, error, allowNegative = false, disabled, placeholder = '0.00', className = '', autoFocus, strict = false }) => {
  const autoId = React.useId()
  const inputId = id ?? autoId
  const errId = `${inputId}-err`
  const normalise = (): void => {
    if (strict) {
      const r = parsePoundsStrict(value)
      if (r.ok) { const next = penceToPoundsText(r.pence); if (next !== value) onChange(next) }
      return
    }
    const neg = value.startsWith('-')
    const p = parsePounds(neg ? value.slice(1) : value)
    if (p == null) return
    const next = penceToPoundsText(neg ? -p : p)
    if (next !== value) onChange(next)
  }
  return (
    <span className={`block ${className}`}>
      <span className="relative block">
        <span aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-casevo-text-soft pointer-events-none select-none">£</span>
        <input
          id={inputId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errId : undefined}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(e) => onChange(strict ? e.target.value : sanitizeMoneyText(e.target.value, allowNegative))}
          onBlur={normalise}
          className={`${INPUT} pl-7 text-right tabular-nums disabled:opacity-60 ${error ? '!border-casevo-danger' : ''}`}
        />
      </span>
      {error && <span id={errId} role="alert" className={FIELD_ERROR}>{error}</span>}
    </span>
  )
}

export const CardHead: React.FC<{ icon: BIconName; title: string; sub?: React.ReactNode; right?: React.ReactNode }> = ({ icon, title, sub, right }) => (
  <div className="flex items-center gap-3 px-5 pt-4 pb-3 flex-wrap">
    <div className="w-[34px] h-[34px] rounded-[10px] bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8] grid place-items-center shrink-0"><BIcon name={icon} /></div>
    <div className="flex-1 min-w-[160px]">
      <h3 className="m-0 text-[15.5px] font-semibold text-casevo-forest">{title}</h3>
      {sub && <p className="m-0 mt-[2px] text-[12.5px] text-casevo-text-soft">{sub}</p>}
    </div>
    {right && <div className="flex items-center gap-2 shrink-0">{right}</div>}
  </div>
)

// A £ figure covering only the viewer's OWN items (colleagues' £ withheld
// server-side), so it's never read as the full figure: the lock + "partial —
// some items hidden", on every billing screen.
export const YOURS_ONLY_TIP = 'Your items only — colleagues’ billing is hidden from you'
export const PARTIAL_NOTE = 'partial — some items hidden'
export const YoursOnly: React.FC = () => (
  <span className="ml-[6px] inline-flex items-center gap-[3px] align-middle text-[11.5px] font-normal text-casevo-text-soft whitespace-nowrap" title={YOURS_ONLY_TIP}>
    <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
    {PARTIAL_NOTE}
  </span>
)
// ★ Restricted viewer: a £ total over only their items (partial). Never shown
// bare — £0 (none of it theirs) would read as "nothing to bill", so it's the
// lock + dash; any other figure carries the "partial — some items hidden" note.
// Not partial → the plain figure, exactly as before.
export const partialMoney = (pence: number | null | undefined, partial: boolean | undefined, size?: number): React.ReactNode =>
  pence == null || (partial && pence === 0) ? <WithheldAmount size={size} /> : partial ? <>{money(pence)}<YoursOnly /></> : money(pence)
// A £ the server hid from this viewer (null) → the lock + dash, never a fake £0.
export const moneyOrHidden = (pence: number | null | undefined, size?: number): React.ReactNode =>
  pence == null ? <WithheldAmount size={size} /> : money(pence)
// The same for plain-text places (titles, confirm messages).
export const moneyText = (pence: number | null | undefined): string => (pence == null ? 'an amount hidden from you' : money(pence))

export const KpiTile: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'danger'; children?: React.ReactNode }> = ({ label, value, sub, tone, children }) => (
  <div className={`${CARD} px-4 py-[14px]`}>
    <div className={`${EYEBROW} ${tone === 'danger' ? '!text-casevo-danger' : ''}`}>{label}</div>
    <div className={`text-[22px] font-semibold tracking-[-0.01em] mt-1 tabular-nums ${tone === 'danger' ? 'text-casevo-danger' : 'text-casevo-forest'}`}>{value}</div>
    {sub && <div className={NOTE}>{sub}</div>}
    {children}
  </div>
)

// ── loading feedback ──
// Placeholder block for skeleton layouts (pulse only when motion is allowed).
export const Skel: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span aria-hidden="true" className={`block rounded-[6px] bg-casevo-border-soft motion-safe:animate-pulse ${className}`} />
)
// Inline busy spinner for buttons ("Issuing…", "Deleting…").
export const Spinner: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" className="motion-safe:animate-spin shrink-0">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
)
// Skeletons shown while a billing view loads (data-skeleton marks them for tests).
export const KpiSkeleton: React.FC<{ tiles?: number }> = ({ tiles = 4 }) => (
  <div data-skeleton className="grid grid-cols-2 lg:grid-cols-4 gap-3" role="status" aria-label="Loading">
    {Array.from({ length: tiles }, (_, i) => (
      <div key={i} className={`${CARD} px-4 py-[14px]`}><Skel className="h-[10px] w-24" /><Skel className="h-[22px] w-28 mt-[10px]" /><Skel className="h-[10px] w-36 mt-[9px]" /></div>
    ))}
  </div>
)
export const RowsSkeleton: React.FC<{ rows?: number }> = ({ rows = 6 }) => (
  <div data-skeleton className={`${CARD} px-4 py-2`} aria-hidden="true">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-4 py-[13px] border-b border-casevo-border-soft last:border-b-0">
        <Skel className="h-[12px] w-24" /><Skel className="h-[12px] flex-1 max-w-[260px]" /><Skel className="h-[12px] w-16 ml-auto" /><Skel className="h-[18px] w-16 rounded-full" />
      </div>
    ))}
  </div>
)

// ── status pill (mockup .st) ──
const ST = 'inline-flex items-center gap-[5px] text-[11.5px] font-semibold px-[9px] py-[2px] rounded-full whitespace-nowrap'
// Stage 2 — what a SETTLED bill actually is (status PAID covers paid, fully
// credited, written off and "client in credit").
// A hidden money position (balance null — a colleague's £ on it) → plain "Paid".
// ★ (Audit) The stored status of a SETTLED bill is PAID whatever settled it
// (server settledStatus: balance ≤ 0). Display-only, derived from the figures:
//   • in credit (balance < 0) → "In credit (£X)" — the refund is handled in
//     the firm's accounts system;
//   • written off → "Written off";
//   • credited in full with nothing actually paid → "Credited";
//   • its money position hidden from you (a colleague's billing on it) →
//     "Settled" — true without giving a figure away;
//   • otherwise → undefined = "Paid".
export const settledLabel = (b: { status: BillStatus; balancePence: number | null; totalPence: number; creditedPence?: number | null; writtenOffPence?: number | null }): string | undefined => {
  if (b.status !== 'PAID') return undefined
  if (b.balancePence == null) return 'Settled'
  if (b.balancePence < 0) return `In credit (${money(-b.balancePence)})`
  if ((b.writtenOffPence ?? 0) > 0) return 'Written off'
  if ((b.creditedPence ?? 0) >= b.totalPence && b.totalPence > 0) return 'Credited'
  return undefined
}
// How far a credit of `creditPence` would put the client in credit (pence > 0),
// given the bill's current balance — null when it wouldn't, or the balance is
// hidden. For the non-blocking "puts the client in credit" warning.
export const inCreditBy = (balancePence: number | null | undefined, creditPence: number | null | undefined): number | null => {
  if (balancePence == null || creditPence == null || creditPence <= 0) return null
  const after = balancePence - creditPence
  return after < 0 ? -after : null
}
export const IN_CREDIT_WARNING = (pence: number): string => `This puts the client in credit by ${money(pence)} — the refund is handled in your accounts system.`
export const StatusPill: React.FC<{ status: BillStatus; overdue?: boolean; awaiting?: boolean; settled?: string }> = ({ status, overdue, awaiting, settled }) => {
  let cls = 'bg-casevo-border-soft text-casevo-text-soft'
  let label = 'Draft'
  // PENDING_APPROVAL (or a draft "sent for approval" before that status existed) → amber.
  if (status === 'PENDING_APPROVAL' || (status === 'DRAFT' && awaiting)) { cls = 'bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]'; label = 'Pending approval' }
  else if (status === 'APPROVED') { cls = 'bg-[#e3f4ea] text-[#1f6b47] dark:bg-[rgba(116,198,157,0.16)] dark:text-[#8fd6b0]'; label = 'Approved' }
  else if ((status === 'ISSUED' || status === 'PART_PAID') && overdue) { cls = 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-[#fca5a5]'; label = 'Overdue' }
  else if (status === 'ISSUED') { cls = 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]'; label = 'Issued' }
  else if (status === 'PART_PAID') { cls = 'bg-transparent text-casevo-brand dark:text-[#8fd6b0] shadow-[inset_0_0_0_1.5px_var(--casevo-light,#74c69d)]'; label = 'Part-paid' }
  // Settled — each way it settled reads differently (settledLabel); only a
  // genuinely paid bill gets the solid "Paid".
  else if (status === 'PAID' && (settled === 'Written off' || settled === 'Settled')) { cls = 'bg-casevo-border-soft text-casevo-text-soft'; label = settled }
  else if (status === 'PAID' && settled === 'Credited') { cls = 'bg-transparent text-casevo-text-soft shadow-[inset_0_0_0_1.5px_var(--casevo-border)]'; label = settled }
  else if (status === 'PAID' && settled?.startsWith('In credit')) { cls = 'bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]'; label = settled }
  else if (status === 'PAID') { cls = 'bg-casevo-primary text-white'; label = settled ?? 'Paid' }
  else if (status === 'VOID') { cls = 'text-casevo-text-soft line-through shadow-[inset_0_0_0_1px_var(--casevo-border)]'; label = 'Void' }
  return <span className={`${ST} ${cls}`}><i className="w-[6px] h-[6px] rounded-full bg-current" aria-hidden="true" />{label}</span>
}
export const DisbStatusPill: React.FC<{ status: string; onDraft?: boolean }> = ({ status, onDraft }) =>
  status === 'BILLED' ? <span className={`${ST} bg-casevo-primary text-white`}><i className="w-[6px] h-[6px] rounded-full bg-current" />Billed</span>
    : status === 'PAID' ? <span className={`${ST} bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]`}><i className="w-[6px] h-[6px] rounded-full bg-current" />{onDraft ? 'Paid · on draft' : 'Paid · ready'}</span>
      : <span className={`${ST} bg-casevo-border-soft text-casevo-text-soft`}><i className="w-[6px] h-[6px] rounded-full bg-current" />Anticipated</span>

// ── VAT chip (mockup .vat) ──
export const VAT_LABEL: Record<VatTreatment, string> = { NONE: 'No VAT', STANDARD: 'VAT 20%', AT_COST: 'VAT at cost' }
export const VAT_HELP: Record<VatTreatment, [string, string]> = {
  NONE: ['No VAT', 'Outside the scope of VAT — e.g. court fees'],
  STANDARD: ['Standard 20%', 'We add 20% — e.g. a medical report you bought in'],
  AT_COST: ['VAT at cost', 'Pass on the exact VAT on the supplier’s invoice'],
}
const VatSwatch: React.FC<{ t: VatTreatment }> = ({ t }) => (
  <i aria-hidden="true" className={`inline-block w-[7px] h-[7px] rounded-[2px] ${t === 'NONE' ? 'shadow-[inset_0_0_0_1.5px_currentColor] text-casevo-text-soft' : t === 'STANDARD' ? 'bg-casevo-brand' : 'shadow-[inset_0_0_0_1.5px_var(--casevo-brand)] bg-[linear-gradient(135deg,var(--casevo-brand)_50%,transparent_50%)]'}`} />
)
export const VatChip: React.FC<{ t: VatTreatment; chevron?: boolean }> = ({ t, chevron }) => (
  <span className="inline-flex items-center gap-[5px] text-[11.5px] font-semibold px-2 py-[2px] rounded-[7px] whitespace-nowrap border border-casevo-border text-casevo-forest bg-casevo-surface">
    <VatSwatch t={t} />{VAT_LABEL[t]}{chevron && <BIcon name="chev" size={11} className="text-casevo-text-soft" />}
  </span>
)

/** Per-line VAT picker — a chip that opens the explained 3-option menu. */
export const VatMenu: React.FC<{ value: VatTreatment; options: VatTreatment[]; atCostPence?: number | null; disabled?: boolean; onChange: (t: VatTreatment) => void }> = ({ value, options, atCostPence, disabled, onChange }) => {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (!open) return
    const off = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', off); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc) }
  }, [open])
  if (disabled) return <VatChip t={value} />
  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="rounded-[7px] focus:outline-none casevo-focus">
        <VatChip t={value} chevron />
      </button>
      {open && (
        <div role="listbox" aria-label="VAT treatment" className={`${CARD} absolute right-0 bottom-full mb-1 z-30 w-[300px] p-[6px] text-left shadow-[0_18px_40px_rgba(0,0,0,0.18)]`}>
          {options.map((t) => (
            <button key={t} type="button" role="option" aria-selected={t === value}
              onClick={() => { setOpen(false); if (t !== value) onChange(t) }}
              className={`w-full flex gap-[10px] items-start text-left px-[10px] py-2 rounded-[8px] focus:outline-none casevo-focus ${t === value ? 'bg-casevo-mint' : 'hover:bg-casevo-input'}`}>
              <span className="mt-[5px]"><VatSwatch t={t} /></span>
              <span className="flex-1">
                <span className="flex text-[13.5px] font-semibold text-casevo-forest">{VAT_HELP[t][0]}{t === value && <BIcon name="check" size={13} className="ml-auto text-casevo-brand" />}</span>
                <span className="block text-[11.5px] text-casevo-text-soft">{VAT_HELP[t][1]}{t === 'AT_COST' && atCostPence != null ? <> — <b className="text-casevo-forest">{money(atCostPence)}</b></> : null}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Activity type pill (mockup .tp) — Call / Letter / Email / Memo…
const TYPE_ICON: Record<string, BIconName> = { call: 'phone', letter: 'pen', email: 'mail', memo: 'memo' }
export const TypePill: React.FC<{ type: string }> = ({ type }) => (
  <span className="inline-flex items-center gap-[5px] whitespace-nowrap text-[10.5px] font-bold tracking-[0.1em] uppercase px-[7px] py-[2px] rounded-[6px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5]">
    <BIcon name={TYPE_ICON[type] ?? 'clock'} size={11} />{type || 'Time'}
  </span>
)
export const TextPill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-[5px] whitespace-nowrap text-[10.5px] font-bold tracking-[0.1em] uppercase px-[7px] py-[2px] rounded-[6px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5]">{children}</span>
)

// Initials avatar for a fee earner (deterministic colour).
const AV_COLOURS = ['#7c5cbf', '#2f7dad', '#c06a3b', '#3f8f6a', '#b5527a', '#5b6bbf']
export const Initials: React.FC<{ name: string; size?: number }> = ({ name, size = 24 }) => {
  const parts = name.trim().split(/\s+/)
  const ini = ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?'
  let h = 0; for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return (
    <span role="img" aria-label={name} title={name} style={{ width: size, height: size, background: AV_COLOURS[h % AV_COLOURS.length], fontSize: size * 0.42 }}
      className="inline-grid place-items-center rounded-full text-white font-bold shrink-0">{ini}</span>
  )
}

// The forest balance band (mockup .baldue).
// pence null → hidden from this viewer: the lock + dash, with hiddenNote (why)
// under it — never a plain figure that reads as "your amount due".
export const BalanceBand: React.FC<{ label?: string; pence: number | null; hiddenNote?: string; trailing?: React.ReactNode }> = ({ label = 'Balance due', pence, hiddenNote, trailing }) => (
  <div className="mt-[10px] rounded-[12px] px-4 py-[14px] text-white flex justify-between items-center bg-gradient-to-br from-[#1a3a2a] to-[#2d6a4f] dark:from-[#1a4230] dark:to-[#2d6a4f]">
    <div>
      <div className="text-[11px] tracking-[0.14em] uppercase font-bold opacity-80">{label}</div>
      {pence == null
        ? <><div className="text-[24px] font-bold tracking-[-0.01em]"><WithheldAmount size={18} className="!text-white/85 !font-bold" /></div>
          {hiddenNote && <div className="text-[11.5px] opacity-80 mt-[2px]">{hiddenNote}</div>}</>
        : <div className="text-[24px] font-bold tracking-[-0.01em] tabular-nums">{money(pence)}</div>}
    </div>
    {trailing}
  </div>
)

export const TotRow: React.FC<{ k: React.ReactNode; v: React.ReactNode; sub?: boolean; big?: boolean }> = ({ k, v, sub, big }) => (
  <div className={`flex justify-between items-baseline gap-3 tabular-nums ${sub ? 'text-[12.5px] py-[3px] pl-3' : big ? 'border-t border-casevo-border mt-[6px] pt-3 text-[16px] font-bold' : 'text-[14px] py-[7px]'}`}>
    <span className={big ? 'text-casevo-forest' : 'text-casevo-text-soft'}>{k}</span><span className="text-casevo-forest whitespace-nowrap">{v}</span>
  </div>
)

// (Audit fix batch 2, Low 12) The server's sentence, else the caller's plain
// fallback — never a raw transport message ("Network Error", "Request failed
// with status code 502", a timeout) in front of a user.
export const RAW_TRANSPORT_ERROR = /^Request failed with status code \d+$|^Network Error$|^timeout of \d+ms exceeded$|^Failed to fetch$/
export const errorText = (e: unknown, fallback: string): string => {
  const ax = e as { response?: { data?: { error?: string } }; message?: string }
  const server = ax?.response?.data?.error
  if (server) return server
  const msg = e instanceof Error ? e.message : ''
  return msg && !RAW_TRANSPORT_ERROR.test(msg) ? msg : fallback
}
